import { faker } from '@faker-js/faker';
import React, { useContext, useEffect, useState } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { CircularProgressbar } from "react-circular-progressbar";
import { withTranslation } from "react-i18next";
import { AiOutlineFileText } from "react-icons/ai";
import { BsInfoCircle } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { RegulationContext } from "../../../App";
import { AnalyticsReportType, CompanyDashboardGraphType, LS_AUTHORITY_KEY, METRIC_CODE } from "../../../core/constants/common";
import CompanyDashboardService from "../../../core/service/CompanyDashboardService";
import graph_Energy from "../../../images/Average Energy Intensity.png";
import graph_Intensity from "../../../images/Energy Intensity.png";
import "./../CompanyDashboard/CompanyDashboard.scss";
import EmptyGraphDataCard from "./EmptyGraphDataCard";

const isDemoEnv = process.env.REACT_APP_ENV === 'demo';

const CompanyGraphCard = ({ t, type, year }) => {
  const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];  
  const monthLabels = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const stackedoptions = {
    plugins: {
      title: {
        display: false,
      },
    },
    responsive: true,
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
  };
  const graphoptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
        display: false,
      },
      title: {
        display: true,
        text: "",
      },
    },
  };

  const emissionAcrossSiteData = {
    labels: ['Mine 1 -', 'Mine 2 -', 'Mine 3 -', 'Mine 4 -', 'Mine 5 -'],
    datasets: [
      {
        label: 'Scope 1',
        data: labels.map(() => faker.datatype.number({ max: 1000 })),
        backgroundColor: '#C1EDC0',
        borderColor: '#07B401',
        borderWidth: 1.5,
      },
      {
        label: 'Scope 2',
        data: labels.map(() => faker.datatype.number({ max: 1000 })),
        backgroundColor: '#D7E5FD',
        borderColor: '#397FF4',
        borderWidth: 1.5,
      },
      {
        label: 'Scope 3',
        data: labels.map(() => faker.datatype.number({ max: 1000 })),
        backgroundColor: 'rgba(255, 206, 86, 0.2)',
        borderColor: 'rgba(255, 206, 86, 1)',
        borderWidth: 1.5,
      },
    ],
  };

  const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const companyId = lsData.profile.company.id;

  const { glbRegulation, setGlbRegulation } = useContext(RegulationContext);
  const [singleValueData, setSingleValueData] = useState(0);
  const [barchartData, setBarchartData] = useState({ labels:[], datasets: []}); 
  const [socialEmpData, setSocialEmpData] = useState({});
  const [doughnutChartData, setDoughnutChartData] = useState({ labels:[], datasets: []});
  const [emptyGraph, setEmptyGraph] = useState(false);
  const [totalEnergyIntake, setTotalEnergyIntake] = useState(0);
  const [linechartData, setLinechartData] =  useState({ labels:[], datasets: []});
  const [graphFooter, setGraphFooter] = useState({ total: 0, comparison: 0 });  

  const navigate = useNavigate();

  useEffect(() => {
    if (!glbRegulation?.id) return;

    loadGraphData(type, year);
  }, [type, year, glbRegulation?.id])

  const loadGraphData = (graphType, filterYear) => {
    if (graphType === CompanyDashboardGraphType.AVG_EENRGY_INT_BY_ORE_MILLED) {
      getEnergyIntensityOremilled(filterYear);
    } else if (graphType === CompanyDashboardGraphType.ENERGY_INT_BY_METAL_PROD) {
      getEnergyIntensityWithMetal(filterYear);
    } else if (graphType === CompanyDashboardGraphType.SCOPE_123_EMISSION) {
      getScope123Emission(filterYear);
    } else if (graphType === CompanyDashboardGraphType.SOCIAL_EMPLOYMENT) {
      getSocialEmployment(filterYear);
    } else if (graphType === CompanyDashboardGraphType.ECONO_VALUE_DISTRIBUTED) {
      getEconomicValueDistributed(filterYear);
    } else if (graphType === CompanyDashboardGraphType.ECONO_VALUE_GENERATED) {
      getEconomicValueByLocation(filterYear);
    } else if (graphType === CompanyDashboardGraphType.WASTE_DISPOSAL) {
      getWasteDisposedInOrg(filterYear);
    } else if (graphType === CompanyDashboardGraphType.ENERGY_PRODUCED) {
      getEnergyProducedData(filterYear);
    } else if (graphType === CompanyDashboardGraphType.SUSTAINABLE_FINANCE) {
      getSustainableFinance(filterYear);
    } else if (graphType === CompanyDashboardGraphType.ENERGY_CONS_OUTSIDE_ORG) {
      getEnergyConsumptionOutside(filterYear);
    } else if (graphType === CompanyDashboardGraphType.WASTE_GEN_IN_ORG) {
      getWasteGeneratedInOrg(filterYear);
    } else if (graphType === CompanyDashboardGraphType.TOTAL_WATER_CONS_IN_ORG) {
      getWaterConsumptionInOrg(filterYear);
    } else if (graphType === CompanyDashboardGraphType.ENERGY_CONS_WITHIN_ORG) {
      getEnergyConsumptionWithin(filterYear);
    }
  };

  const getEnergyIntensityOremilled = (filterYear) => {
    CompanyDashboardService.getEnergyIntensityOremilled(glbRegulation.id, `${filterYear}-01-01`)
      .then((response) => {
        if (response.status !== 200) return;

        let total = 0, count = 0;
        Object.keys(response.data.data)?.forEach(key => {
          total += response.data.data[key].oreMilled;
          count ++;          
        });
        setSingleValueData((total/count).toFixed(2));
      })
      .catch((error) => {
      });
  };

  const getEnergyIntensityWithMetal = (filterYear) => {
    CompanyDashboardService.getEnergyIntensityWithMetal(glbRegulation.id, `${filterYear}-01-01`)
      .then((response) => {
        if (response.status !== 200) return;

        let total = 0, count = 0;
        Object.keys(response.data.data)?.forEach(key => {
          total += response.data.data[key].metalProduced;
          count ++;          
        });
        setSingleValueData((total/count).toFixed(2));
      })
      .catch((error) => {
      });
  };

  const getScope123Emission = (filterYear) => {
    CompanyDashboardService.getScope123Emission(glbRegulation.id, `${filterYear}-01-01`)
      .then((response) => {
        if (response.status !== 200) return;

        setBarchartData({
          labels: ['Company'],
          datasets: [
            {
              label: 'Scope 1',
              data: labels.map(() => response.data.data.scope1),
              backgroundColor: '#C1EDC0',
              borderColor: '#07B401',
              borderWidth: 1.5,
            },
            {
              label: 'Scope 2',
              data: labels.map(() => response.data.data.scope2),
              backgroundColor: '#D7E5FD',
              borderColor: '#397FF4',
              borderWidth: 1.5,
            },
            {
              label: 'Scope 3',
              data: labels.map(() => response.data.data.scope3),
              backgroundColor: 'rgba(255, 206, 86, 0.2)',
              borderColor: 'rgba(255, 206, 86, 1)',
              borderWidth: 1.5,
            },
          ],
        });
      })
      .catch((error) => {
      });
  };

  const getSocialEmployment = (filterYear) => {
    CompanyDashboardService.getSocialEmployment(glbRegulation.id, `${filterYear}-01-01`)
      .then((response) => {
        if (response.status !== 200) return;

        setSocialEmpData(response.data.data);
      })
      .catch((error) => {
      });
  };

  const getEconomicValueDistributed = (filterYear) => {
    CompanyDashboardService.getEconomicValueDistributed(glbRegulation.id, `${filterYear}-01-01`)
      .then((response) => {
        if (response.status !== 200) return;

        const resData = response.data.data;
        let graphData = {
          options: {
            responsive: true,
            plugins: {
              legend: {
                position: "top",
                display: false,
              },
              title: {
                display: true,
                text: "",
              },
            },
          },

          labels: [
            'Operating costs',
            'Employee wages and benefits,',
            'Payments to providers of capital',
            'Payments to government by country',
            'Community investments',
          ],
          datasets: [
            {
              label: '# of Votes',
              data: [
                resData['ECONOMIC_VALUE_GEN_OPERATING_COST'],
                resData['ECONOMIC_VALUE_GEN_WAGES_AND_BENEFITS'],
                resData['ECONOMIC_VALUE_GEN_PAYMENT_PROVIDER_BY_CAPITAL'], 
                resData['ECONOMIC_VALUE_GEN_PAYMENT_PROVIDER_BY_COUNTRY'],
                resData['TOTAL_ECONOMIC_VALUE_DISTRIBUTED']
              ],
              backgroundColor: [
                '#D7E5FD',
                '#C1EDC0',
                'rgba(255, 206, 86, 0.2)',
                'rgba(75, 192, 192, 0.2)',
                'rgba(153, 102, 255, 0.2)',
              ],
              borderColor: [
                '#397FF4',
                '#07B401',
                'rgba(255, 206, 86, 1)',
                'rgba(75, 192, 192, 1)',
                'rgba(153, 102, 255, 1)',
              ],
              borderWidth: 1.5,
            },
          ],
        }
        setDoughnutChartData(graphData);
      })
      .catch((error) => {
      });
  };

  const getEconomicValueByLocation = (filterYear) => {
    CompanyDashboardService.getEconomicValueByLocation(glbRegulation.id, `${filterYear}-01-01`)
      .then((response) => {
        if (response.status !== 200) return;

        let keyArray = [];
        let economicValGenArray = [];
        let economicValDistributedArray = [];
        for (const [key, value] of Object.entries(response['data'].data)) {
          keyArray.push(key);
          economicValGenArray.push(value['ECONOMIC_VALUE_GENERATED']);
          economicValDistributedArray.push(value['ECONOMIC_VALUE_DISTRIBUTED']);
        }
        let graphData = {
          options: {
            responsive: true,
            plugins: {
              legend: {
                position: "top",
                display: false,
              },
              title: {
                display: true,
                text: "",
              },
            },
          },

          labels: keyArray,
          datasets: [
            {
              label: 'Direct economic value generated: revenues',
              data: economicValGenArray,
              backgroundColor: '#D7E5FD',
              borderColor: '#397FF4',
              borderWidth: 1.5,

            },
            {
              label: 'Total economic value distributed',
              data: economicValDistributedArray,
              backgroundColor: '#C1EDC0',
              borderColor: '#07B401',
              borderWidth: 1.5,
            },

          ],
        }
        setBarchartData(graphData);
      })
      .catch((error) => {
      });
  };
  
  const getWasteDisposedInOrg = (filterYear) => {
    CompanyDashboardService.getWasteDisposedInOrg(glbRegulation.id, `${filterYear}-01-01`)
      .then((response) => {
        if (response.status !== 200) return;

        let resData = response['data'].data;
        // return
        let graphData = {
          options: {
            responsive: true,
            plugins: {
              legend: {
                position: "top",
                display: false,
              },
              title: {
                display: true,
                text: "",
              },
            },
          },
          labels: labels,
          datasets: [
            {
              type: 'bar',
              label: 'Waste diverted from disposal ',
              data: [
                resData['January'].WASTE_DIVERTED_FROM_DISPOSAL, 
                resData['February'].WASTE_DIVERTED_FROM_DISPOSAL,
                resData['March'].WASTE_DIVERTED_FROM_DISPOSAL, 
                resData['April'].WASTE_DIVERTED_FROM_DISPOSAL,
                resData['May'].WASTE_DIVERTED_FROM_DISPOSAL, 
                resData['June'].WASTE_DIVERTED_FROM_DISPOSAL,
                resData['July'].WASTE_DIVERTED_FROM_DISPOSAL, 
                resData['August'].WASTE_DIVERTED_FROM_DISPOSAL,
                resData['September'].WASTE_DIVERTED_FROM_DISPOSAL, 
                resData['October'].WASTE_DIVERTED_FROM_DISPOSAL,
                resData['November'].WASTE_DIVERTED_FROM_DISPOSAL, 
                resData['December'].WASTE_DIVERTED_FROM_DISPOSAL
              ],
              backgroundColor: '#D7E5FD',
              borderColor: '#397FF4',
              borderWidth: 1.5,
            },
            {
              type: 'bar',
              label: 'Waste directed to disposal',
              data: [
                resData['January'].WASTE_DIRECTED_FROM_DISPOSAL, 
                resData['February'].WASTE_DIRECTED_FROM_DISPOSAL,
                resData['March'].WASTE_DIRECTED_FROM_DISPOSAL, 
                resData['April'].WASTE_DIRECTED_FROM_DISPOSAL,
                resData['May'].WASTE_DIRECTED_FROM_DISPOSAL, 
                resData['June'].WASTE_DIRECTED_FROM_DISPOSAL,
                resData['July'].WASTE_DIRECTED_FROM_DISPOSAL, 
                resData['August'].WASTE_DIRECTED_FROM_DISPOSAL,
                resData['September'].WASTE_DIRECTED_FROM_DISPOSAL, 
                resData['October'].WASTE_DIRECTED_FROM_DISPOSAL,
                resData['November'].WASTE_DIRECTED_FROM_DISPOSAL, 
                resData['December'].WASTE_DIRECTED_FROM_DISPOSAL
              ],
              backgroundColor: '#dcdcdc',
              borderColor: '#808080',
              borderWidth: 1.5,
            },
          ],
        }
        setBarchartData(graphData);
      })
      .catch((error) => {
      });
  };

  const getEnergyProducedData = (filterYear) => {
    CompanyDashboardService.getEnergyProducedData(glbRegulation.id, `${filterYear}-01-01`)
      .then((response) => {
        const resData = response['data'].data;
        setTotalEnergyIntake(resData['TOTAL_ENERGY_INTAKE']);
        setDoughnutChartData({
          options: {
            responsive: true,
            plugins: {
              legend: {
                position: "top",
                display: false,
              },
              title: {
                display: true,
                text: "",
              },
            },
          },
          labels: ['Electricity generation fossil fuels', 'Renewable electricity consumed', 'Onsite fuels and oils consumed', 'Transport fuels consumed', 'Energy purchased'],
          datasets: [
            {
              label: '# of Votes',
              data: [resData['ELECTRICITY_GENERATION_FOSSIL_FUELS'], resData['RENEWABLE_ELECTRICITY_CONSUMED'], resData['ONSITE_FUELS_AND_OILS_CONSUMED'], resData['TRANSPORT_FUELS_CONSUMED'], resData['ENERGY_PURCHASED']],
              backgroundColor: [
                '#C1EDC0',
                '#D7E5FD',
                'rgba(255, 206, 86, 0.2)',
                'rgba(75, 192, 192, 0.2)',
                'rgba(153, 102, 255, 0.2)',
              ],
              borderColor: [
                '#07B401',
                '#397FF4',
                'rgba(255, 206, 86, 1)',
                'rgba(75, 192, 192, 1)',
                'rgba(153, 102, 255, 1)',
              ],
              borderWidth: 1.5,
            },
          ],
        });
        if (resData['ELECTRICITY_GENERATION_FOSSIL_FUELS'] === 0 
         && resData['ENERGY_PURCHASED'] === 0 
         && resData['ONSITE_FUELS_AND_OILS_CONSUMED'] === 0 
         && resData['RENEWABLE_ELECTRICITY_CONSUMED'] === 0 
         && resData['TRANSPORT_FUELS_CONSUMED'] === 0 ) {
          setEmptyGraph(true);
        } else {
          setEmptyGraph(false);
        }
      })
      .catch((error) => {
      });
  };

  const getSustainableFinance = (filterYear) => {
    CompanyDashboardService.getMonthlyDPBasedGraph(glbRegulation.id, `${filterYear}-01-01`, 'SUSTAINABLE_FINANCE')
    .then((response) => {
        const resData = response['data'].data;
        const data1 = [], data2 = [], data3 = [], data4 = [];
        for (let i = 0; i < monthLabels?.length; i++) {
          const month = monthLabels[i];
          data1.push(resData[month].accessToEssentialServices);
          data2.push(resData[month].renewableEnergy);
          data3.push(resData[month].socioEconomicAdvancementAndEmpowerment);
          data4.push(resData[month].sustainableWaterAndWasteWaterTreatment);
        }
        const obj = {
          labels,
          datasets: [
            {
              label: 'Access to essential services',
              data: data1,
              borderColor: '#07B401',
              backgroundColor: '#C1EDC0',
            },
            {
              label: 'Renewable energy',
              data: data2,
              borderColor: '#397FF4',
              backgroundColor: '#D7E5FD',
            },
            {
              label: 'Socio economic advancement and empowerment',
              data: data3,
              borderColor: 'rgba(255, 206, 86, 1)',
              backgroundColor: 'rgba(255, 206, 86, 0.5)',
            },
            {
              label: 'Sustainable water and waste water treatment',
              data: [resData['January'].sustainableWaterAndWasteWaterTreatment, resData['February'].sustainableWaterAndWasteWaterTreatment,
              resData['March'].sustainableWaterAndWasteWaterTreatment, resData['April'].sustainableWaterAndWasteWaterTreatment,
              resData['May'].sustainableWaterAndWasteWaterTreatment, resData['June'].sustainableWaterAndWasteWaterTreatment,
              resData['July'].sustainableWaterAndWasteWaterTreatment, resData['August'].sustainableWaterAndWasteWaterTreatment,
              resData['September'].sustainableWaterAndWasteWaterTreatment, resData['October'].sustainableWaterAndWasteWaterTreatment,
              resData['November'].sustainableWaterAndWasteWaterTreatment, resData['December'].sustainableWaterAndWasteWaterTreatment],
              borderColor: 'rgba(153, 102, 255, 0.2)',
              backgroundColor: 'rgba(153, 102, 255, 1)',
            },
          ],
        }
        setLinechartData(obj);
        let hasData = false;
        let legendKeys = ['accessToEssentialServices', 'renewableEnergy', 'socioEconomicAdvancementAndEmpowerment', 'sustainableWaterAndWasteWaterTreatment'];
        for (let i = 0; i < legendKeys?.length; i++) {
          const legend = legendKeys[i];
          for (let j = 0; j < monthLabels?.length; j++) {
            const month = monthLabels[j];
            if (resData[month][legend] > 0) {
              hasData = true;
              break;
            }
          }
        }
      })
      .catch((error) => {
      });
  };

  const getEnergyConsumptionOutside = (filterYear) => {
    CompanyDashboardService.getEnergyConsumptionOutside(glbRegulation.id, `${filterYear}-01-01`)
      .then((response) => {
        const resData = response['data'].data;
        const data1 = [], data2 = [], data3 = [];
        for (let i = 0; i < monthLabels?.length; i++) {
          const month = monthLabels[i];
          data1.push(resData[month].upstreamEnergyConsumption);
          data2.push(resData[month].downstreamEnergyConsumption);
          data3.push(resData[month].totalEnergyConsumption);
        }
        setLinechartData({
          labels,
          datasets: [
            {
              label: 'Upstream energy consumption',
              data: data1,
              borderColor: '#07B401',
              backgroundColor: '#C1EDC0',
            },
            {
              label: 'Downstream energy consumption',
              data: data2,
              borderColor: '#397FF4',
              backgroundColor: '#D7E5FD',
            },
            {
              label: 'Total energy consumption',
              data: data3,
              borderColor: 'rgba(255, 206, 86, 1)',
              backgroundColor: 'rgba(255, 206, 86, 0.5)',
            },
          ],
        });
        setGraphFooter({ 
          total: resData.graphFooter.totalEnergyConsumption, 
          comparison: resData.graphFooter.comparedToPreviousYear, 
        });
      })
      .catch((error) => {
      });
  };

  const getWasteGeneratedInOrg = (filterYear) => {
    CompanyDashboardService.getWasteGeneratedInOrg(glbRegulation.id, `${filterYear}-01-01`)
      .then((response) => {
        const resData = response['data'].data;
        const data1 = [], data2 = [], data3 = [];
        for (let i = 0; i < monthLabels?.length; i++) {
          const month = monthLabels[i];
          data1.push(resData[month].HAZARDOUS_WASTE_GENERATED);
          data2.push(resData[month].NON_HAZARDOUS_WASTE_GENERATED);
          data3.push(resData[month].TOTAL_WASTE_GENERATED);
        }
        setBarchartData({
          options: {
            responsive: true,
            plugins: {
              legend: {
                position: "top",
                display: false,
              },
              title: {
                display: true,
                text: "",
              },
            },
          },
          labels: labels,
          datasets: [
            {
              type: 'bar',
              label: 'Hazardous Waste Generated',
              data: data1,
              backgroundColor: '#D7E5FD',
              borderColor: '#397FF4',
              borderWidth: 1.5,

            },
            {
              type: 'bar',
              label: 'Non-hazardous Waste Generated',
              data: data2,
              backgroundColor: '#dcdcdc',
              borderColor: '#808080',
              borderWidth: 1.5,
            },
            {
              type: 'line',
              label: 'Total Waste Generated',
              data: data3,
              backgroundColor: '#D7E5FD',
              borderColor: '#397FF4',
              borderWidth: 1.5,
            }
          ],
        });
      })
      .catch((error) => {
      });
  };

  const getWaterConsumptionInOrg = (filterYear) => {
    let req;
    // TODO >> Handle this properly after UAT release
    if (isDemoEnv && (companyId === 150 || companyId === 151)) {
      req = CompanyDashboardService.getMonthlyDPBasedGraph(glbRegulation.id, `${filterYear}-01-01`, 'TOTAL_WATER_CONSUMPTION_IN_THE_ORGANIZATION');
    } else  {
      req = CompanyDashboardService.getWaterConsumption(glbRegulation.id, `${filterYear}-01-01`);
    }
    req?.then((response) => {
        const resData = response['data'].data;
        let datasets = [];
        if (isDemoEnv && (companyId === 150 || companyId === 151)) {
          const data1 = [], data2 = [], data3 = [];
          for (let i = 0; i < monthLabels?.length; i++) {
            const month = monthLabels[i];
            data1.push(resData[month].municipalWater);
            data2.push(resData[month].groundWater);
            data3.push(resData[month].surfaceWater);
          }
          datasets = [
            {
              label: 'Municipal Water',
              data: data1,
              borderColor: '#07B401',
              backgroundColor: '#C1EDC0',
              borderWidth: 1.5,

            },
            {
              label: 'Ground Water',
              data: data2,
              backgroundColor: '#D7E5FD',
              borderColor: '#397FF4',
              borderWidth: 1.5,
            },
            {
              label: 'Surface Water',
              data: data3,
              borderColor: 'rgba(255, 206, 86, 1)',
              backgroundColor: 'rgba(255, 206, 86, 0.5)',
              borderWidth: 1.5,
            },
          ];
        } else {
          const data1 = [], data2 = [], data3 = [];
          for (let i = 0; i < monthLabels?.length; i++) {
            const month = monthLabels[i];
            data1.push(resData[month].TOTAL_WATER_WITHDRAWAL);
            data2.push(resData[month].TOTAL_WATER_DISCHARGED);
            data3.push(resData[month].TOTAL_WATER_CONSUMED);
          }
          datasets = [
            {
              label: 'Total Water Withdrawal',
              data: data1,
              borderColor: '#07B401',
              backgroundColor: '#C1EDC0',
              borderWidth: 1.5,

            },
            {
              label: 'Total Water Discharged',
              data: data2,
              backgroundColor: '#D7E5FD',
              borderColor: '#397FF4',
              borderWidth: 1.5,
            },
            {
              label: 'Total Water Consumed',
              data: data3,
              borderColor: 'rgba(255, 206, 86, 1)',
              backgroundColor: 'rgba(255, 206, 86, 0.5)',
              borderWidth: 1.5,
            },
          ];
        }
        setLinechartData({
          options: {
            responsive: true,
            plugins: {
              legend: {
                position: "top",
                display: false,
              },
              title: {
                display: true,
                text: "",
              },
            },
          },
          labels: labels,
          datasets: datasets,
        });
      })
      .catch((error) => {
      });
  };

  const getEnergyConsumptionWithin = (filterYear) => {
    let req;
    // TODO >> Handle this properly after UAT release
    if (isDemoEnv && (companyId === 150 || companyId === 151)) {
      req = CompanyDashboardService.getMonthlyDPBasedGraph(glbRegulation.id, `${filterYear}-01-01`, 'ENERGY_CONSUMPTION_WITHIN_THE_ORGANIZATION_V2');
    } else  {
      req = CompanyDashboardService.getEnergyConsumptionWithin(glbRegulation.id, `${filterYear}-01-01`);
    }
    req?.then((response) => {
        const resData = response['data'].data;
        let datasets = [];
        // TODO Remove later >> BAD Practice
        if (isDemoEnv && (companyId === 150 || companyId === 151)) {
          const data1 = [], data2 = [], data3 = [];
          for (let i = 0; i < monthLabels?.length; i++) {
            const month = monthLabels[i];
            data1.push(resData[month].electricityGenerationFossilFuels);
            data2.push(resData[month].fuelConsumptionOfBankVehicleAndGenerator);
            data3.push(resData[month].renewableElectricityConsumed);
          }
          datasets = [
            {
              label: 'Fossil fuel based electricity consumed',
              data: data1,
              borderColor: '#07B401',
              backgroundColor: '#C1EDC0',
            },
            {
              label: 'Fuel consumption of Bank Vehicles/Backup Generator',
              data: data2,
              borderColor: '#397FF4',
              backgroundColor: '#D7E5FD',
            },
            {
              label: 'Renewable electricity consumed',
              data: data3,
              borderColor: 'rgba(255, 206, 86, 1)',
              backgroundColor: 'rgba(255, 206, 86, 0.5)',
            },
          ];
          setGraphFooter({
            total: resData.graphFooter?.totalEnergyConsumption ?? 0, 
            comparison: resData.graphFooter?.comparedToPreviousYear ?? 0, 
          })
        } else {
          const data1 = [], data2 = [], data3 = [];
          for (let i = 0; i < monthLabels?.length; i++) {
            const month = monthLabels[i];
            data1.push(resData[month].energyIntake);
            data2.push(resData[month].energySold);
            data3.push(resData[month].totalConsumption);
          }
          datasets = [
            {
              label: 'Energy intake',
              data: data1,
              borderColor: '#07B401',
              backgroundColor: '#C1EDC0',
            },
            {
              label: 'Energy sold',
              data: data2,
              borderColor: '#397FF4',
              backgroundColor: '#D7E5FD',
            },
            {
              label: 'Total energy consumption',
              data: data3,
              borderColor: 'rgba(255, 206, 86, 1)',
              backgroundColor: 'rgba(255, 206, 86, 0.5)',
            },
          ];
        }
        setLinechartData({
          labels,
          datasets: datasets,
        });
      })
      .catch((error) => {
      });
  };

  const getTooltip = () => {
    return (
      <Popover id="popover-trigger-hover-focus" className="p-2">
        <div className="p-1">
        {
          type === CompanyDashboardGraphType.AVG_EENRGY_INT_BY_ORE_MILLED 
          ? 'Average energy intensity by ore milled'
          : type === CompanyDashboardGraphType.ENERGY_INT_BY_METAL_PROD
          ? 'Energy intensity by metal produced'
          : type === CompanyDashboardGraphType.SCOPE_123_EMISSION
          ? 'Scope 1, Scope 2 & Scope 3 emissions across organization'
          : type === CompanyDashboardGraphType.WASTE_DISPOSAL
          ? 'Waste disposal in the organization (in tons)'
          : type === CompanyDashboardGraphType.ENERGY_PRODUCED
          ? 
          <>
            <b>Energy produced =</b> <br />
            Total energy produced from renewable and non-renewable sources  + <br />
            Energy purchased
          </>
          : type === CompanyDashboardGraphType.SUSTAINABLE_FINANCE
          ? 'Sustainable Finance'
          : type === CompanyDashboardGraphType.ENERGY_CONS_OUTSIDE_ORG
          ? 
          <>
            <b>Total energy consumption =</b> <br />
            Upstream energy consumption + <br />
            Downstream energy consumption
          </>
          : type === CompanyDashboardGraphType.WASTE_GEN_IN_ORG
          ? 'Waste generated in the organization (in tons)'
          : type === CompanyDashboardGraphType.SCOPE_123_EMISSION_ACROSS_SITE
          ? 'Scope 1, Scope 2 & Scope 3 emissions across site'
          : type === CompanyDashboardGraphType.TOTAL_WATER_CONS_IN_ORG
          ? 'Total water consumption in the organization (in Megaliters)'
          : type === CompanyDashboardGraphType.ENERGY_CONS_WITHIN_ORG
          ? 
          <>
            <div>
              <b>Energy intake =</b><br />
              Electricity generation fossil fuels +<br />
              Renewable electricity consumed +<br />
              Onsite fuels and oils consumed +<br />
              Transport fuels consumed +<br />
              Energy purchased
            </div>
            <div>
              <b>Total energy consumption =</b><br />
              Energy intake - Energy sold
            </div>
          </>
          :
          ''
        }
        </div>
      </Popover>
    )
  };

  const setMetricCode = (id) => {
    localStorage.setItem(METRIC_CODE, id);
  };

  const analyticsTooltip = (
    <Popover id="popover-trigger-hover-focus" className="p-2">
      <div className="p-1">
        Show details
      </div>
    </Popover>
  );

  const renderEnergyIntensityOremilledGraph = () => {
    return (
      <div className="card  Energy_Intensity_card border-top-0">
        <div className="card-body">
          <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="bottom"
            overlay={getTooltip()}>
            <span className="Overlay_tooltipbox"><BsInfoCircle /></span>
          </OverlayTrigger>
          <h3>{singleValueData} <small> MJ/t</small></h3>
          <h5>Average energy intensity by ore milled</h5>
          <div className="mb-5">
            <img src={graph_Energy} />
          </div>
          <div>
            <h4 className="text-green">-25.5%</h4>
            <b className="m-0">Compared to last year</b>
          </div>
        </div>
      </div>
    );
  };

  const renderEnergyIntensityByMetalProd = () => {
    return (
      <div className="card  Energy_Intensity_card border-top-0">
        <div className="card-body">
          <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="bottom"
            overlay={getTooltip()}>
            <span className="Overlay_tooltipbox"><BsInfoCircle /></span>
          </OverlayTrigger>
          {
            // TODO Remove later >> BAD Practice
            isDemoEnv && (companyId === 150 || companyId === 151) 
            ? <></>
            : <h3>{singleValueData} <small>GJ/oz</small></h3>
          }
          <h5>Energy intensity by metal produced</h5>
          {
            // TODO Remove later >> BAD Practice
            isDemoEnv && (companyId === 150 || companyId === 151)
            ? <EmptyGraphDataCard />
            :
            <>
              <div className="mb-5">
                <img src={graph_Intensity} />
              </div>
              <div>
                <h4 className="text-red">+18.3%</h4>
                <b className="m-0">Compared to last year</b>
              </div>
            </>            
          }          
        </div>
      </div>
    );
  };

  const renderScope123EmissionGraph = () => {
    return (
      <div className="card Energy_Produced_card border-top-0">
        <div className="Energy_Produced_card-header ">
          <h6 className="m-0">Scope 1, Scope 2 & Scope 3 emissions</h6>
          {renderGraphIconHolder(AnalyticsReportType.SCOPE_1_2_3_EMISSION)}
        </div>
        <div className="card-body pt-0 pb-2">
          <div className="">
            <Bar data={barchartData} />
          </div>
          <div className="Energy_Produced_card-footer mt-2">
            <div>
              <h5>0.0140 kCO2 - e/t</h5>
              <b>Emission intensity by ore milled</b>
            </div>
            <div className="Compared_box01">
              <h6>+9.6%</h6>
              <b>Compared to 2022 Q4</b>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSocialEmpGraph = () => {
    return (
      <>
        <div className="col-lg-3 col-md-6 p-0">
          <div className="card  Energy_Intensity_card  border-top-0">
            <div className="card-body">
              <h3>{socialEmpData.averageTrainingHours}</h3>
              <h5>Average training hours per employee</h5>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 p-0">
          <div className="card  Energy_Intensity_card  border-top-0">
            <div className="card-body">
              <div className="Local_EmployementCircularProgressbar_box">
                <CircularProgressbar value={socialEmpData.localEmployment ?? 0} 
                  text={`${socialEmpData.localEmployment ?? 0}%`} strokeWidth={5} />
              </div>
              <h5>Local employement across operations</h5>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderEconomicValueDistributedGraph = () => {
    return (
      <div className="card  Energy_Produced_card">
        <div className="Energy_Produced_card-header">
          <h6 className="m-0">Economic value distributed</h6>
        </div>

        <div className="card-body pt-0 row m-0 align-items-center">
          {
            // TODO Remove later >> BAD Practice
            isDemoEnv && (companyId === 150 || companyId === 151)
            ? <EmptyGraphDataCard/>
            :
            <>
              <div className="col-lg-6 col-md-12 p-2">
                <div className="Energy_Produceddata_graph">
                  <Doughnut data={doughnutChartData} options={graphoptions} />
                </div>
              </div>
              <div className="col-lg-6 col-md-12 p-2">
                <div className="Energy_Produceddata_graph">
                  <ul>
                    <li> <span></span>Employee wages and benefits</li>
                    <li> <span></span>Operating costs </li>
                    <li> <span></span>Payments to providers of capital</li>
                    <li> <span></span>Payments to government by country</li>
                    <li> <span></span>Community investments</li>
                  </ul>
                </div>
              </div>
            </>
          }
        </div>
      </div>
    );
  };

  const renderEconomicValueGenGraph = () => {
    return (
      <div className="card  Energy_Produced_card">
        <div className="Energy_Produced_card-header">
          <h6 className="m-0">Direct economic value generated and distributed  </h6>
        </div>
        <div className=" card-body pt-0 Energy_Produceddata_graph">
          {
            // TODO Remove later >> BAD Practice
            isDemoEnv && (companyId === 150 || companyId === 151)
            ? <EmptyGraphDataCard/>
            : <Bar data={barchartData} />
          }          
        </div>
      </div>
    );
  };

  const renderWasteDisposalGraph = () => {
    return (
      <div className="card Energy_Produced_card border-top-0">
        <div className="Energy_Produced_card-header">
          <h6 className="m-0"> Waste disposal in the organization (in tons)</h6>
          {renderGraphIconHolder(AnalyticsReportType.WASTE_DISPOSAL_IN_ORG)}
        </div>
        <div className="card-body pt-0 pb-2">
          <div className="">
            <Bar data={barchartData} options={stackedoptions} />
          </div>
        </div>
      </div>
    );
  };

  const renderEnergyProducedGraph = () => {
    return (
      <div className="card  Energy_Produced_card">
        <div className="Energy_Produced_card-header">
          <h6 className="m-0">Energy produced</h6>
          {renderGraphIconHolder(AnalyticsReportType.ENERGY_PRODUCED)}
        </div>
        <div className="card-body pt-0 row m-0 align-items-center">
          {
            // TODO Remove later >> BAD Practice
            isDemoEnv && (companyId === 150 || companyId === 151) 
            ? <EmptyGraphDataCard />
            : 
            <>
              <div className="col-lg-6 col-md-12 p-2">
                <div className="Energy_Produceddata_graph">
                  <Doughnut data={doughnutChartData} options={graphoptions} />
                </div>
              </div>
              <div className="col-lg-6 col-md-12 p-2">
                <div className="Energy_Produceddata_graph">
                  <ul>
                    <li>
                      <span></span>
                      Electricity generation fossil fuels
                    </li>
                    <li> <span></span>Renewable electricity consumed</li>
                    <li> <span></span>Onsite fuels and oils consumed</li>
                    <li> <span></span>Transport fuels consumed</li>
                    <li> <span></span>Energy purchased</li>
                  </ul>
                </div>
              </div>
              <div className="Energy_Produced_card-footer mt-2">
                <div>
                  <h5> {totalEnergyIntake} GJ</h5>
                  <b>Total energy intake</b>
                </div>
                <div className="Compared_box01">
                  <h6>+9.6%</h6>
                  <b>Compared to 2022 Q4</b>
                </div>
              </div>
            </>
          }
        </div>
      </div>
    );
  };

  const renderSustainableFinanceGraph = () => {
    return (
      <div className="card Energy_Produced_card border-top-0">
        <div className="Energy_Produced_card-header">
          <h6 className="m-0"> Sustainable Finance</h6>
          {renderGraphIconHolder(AnalyticsReportType.SUSTAINABLE_FINANCE)}
        </div>
        <div className="card-body pt-0 pb-2">
          <div className="">
            <Line data={linechartData} />
          </div>
          {/* <div className="text-center p-2 text-dark">
            Emission intensity by metal product <strong>1.01</strong> (t Scope 1 & Scope 2 Co2-e)
          </div> */}

          {/* <div className="Energy_Produced_card-footer mt-2">
            <div>
              <h5>1.01 kCO2 - e/oz</h5>
              <b>Emission intensity by metal product</b>
            </div>
            <div className="Compared_box01">
              <h6>+9.6%</h6>
              <b>Compared to 2022 Q4</b>
            </div>
          </div> */}
        </div>

      </div>
    );
  };

  const renderEnergyConsumptionOutsideGraph = () => {
    return (
      <div className="card  Energy_Produced_card border-top-0">
        <div className="Energy_Produced_card-header">
          <h6 className="m-0">Energy consumption outside of the organization </h6>
          {renderGraphIconHolder(AnalyticsReportType.ENERGY_CONSUMPTION_OUTSIDE_THE_ORGANIZATION)}
        </div>
        <div className="card-body pt-0">
          <div className="">
            <Line data={linechartData} />
          </div>
          <div className="Energy_Produced_card-footer mt-2">
            <div>
              <h5>{graphFooter.total}</h5>
              <b>Average energy consumption</b>
            </div>
            <div className="Compared_box01">
              <h6>{graphFooter.comparison}%</h6>
              <b>Compared to 2022 Q4</b>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGraphIconHolder = (type) => {
    return (
      <div className='companyDashboard-iconBox'>
        <ul>
          {/* <li>
            <OverlayTrigger
              trigger={['hover', 'focus']}
              placement="bottom"
              overlay={analyticsTooltip}>
              <a href='#' onClick={() => onAnalyticsClick(type)}>
                <span><AiOutlineFileText size={18} /></span>
              </a>
            </OverlayTrigger>
          </li> */}
          <li>
            <OverlayTrigger
              trigger={['hover', 'focus']}
              placement="bottom"
              overlay={getTooltip()}>
              <span><BsInfoCircle /></span>
            </OverlayTrigger>
          </li>
        </ul>
      </div>
    );
  };

  const onAnalyticsClick = (type) => {
    const from = year + '-01-01';
    const currentDate = new Date();
    const to = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1)  < 10 ? '0' : ''}${currentDate.getMonth() + 1}-01`;
    navigate(`/company_admin/analytics_report/type/${type.id}?from=${from}&to=${to}`)
  };

  const renderWasteGeneratedInOrgGraph = () => {
    return (
      <div className="card Energy_Produced_card border-top-0">
        <div className="Energy_Produced_card-header">
          <h6 className="m-0">Waste generated in the organization (in tons)</h6>
          {renderGraphIconHolder(AnalyticsReportType.WASTE_GENERATED_IN_ORG)}
        </div>
        <div className="card-body pt-0 pb-2">
          <div className="">
            <Bar data={barchartData} />
          </div>
          {/* <div className="text-center p-2 text-dark">
            Emission intensity by ore milled <strong>0.0140</strong> (t Scope 1 & Scope 2 Co2-e)
          </div> */}
          {/* <div className="Energy_Produced_card-footer mt-2">
            <div>
              <h5>0.0140 kCO2 - e/t</h5>
              <b>Emission intensity by ore milled</b>
            </div>
            <div className="Compared_box01">
              <h6>+9.6%</h6>
              <b>Compared to 2022 Q4</b>
            </div>
          </div> */}
        </div>

      </div>
    );
  };

  const renderScope123EmissionAcrossSiteGraph = () => {
    return (
      <div className="card Energy_Produced_card border-top-0">
        <div className="Energy_Produced_card-header">
          <h6 className="m-0"> Scope 1, Scope 2 & Scope 3 emissions across site </h6>
          {renderGraphIconHolder(AnalyticsReportType.SCOPE_1_2_3_EMISSION_ACROSS_SITE)}
        </div>
        <div className="card-body pt-0 pb-2">
        {
          // TODO Remove later >> BAD Practice
          isDemoEnv && (companyId === 150 || companyId === 151)
          ? <EmptyGraphDataCard/>
          :
          <>
            <div className="">
              <Bar data={emissionAcrossSiteData} options={stackedoptions} />
            </div>
            {/* <div className="text-center p-2 text-dark">
              Emission intensity by metal product <strong>1.01</strong> (t Scope 1 & Scope 2 Co2-e)
            </div> */}

            <div className="Energy_Produced_card-footer mt-2">
              <div>
                <h5>1.01 kCO2 - e/oz</h5>
                <b>Emission intensity by metal product</b>
              </div>
              <div className="Compared_box01">
                <h6>+9.6%</h6>
                <b>Compared to 2022 Q4</b>
              </div>
            </div>
          </>
        }                        
        </div>
      </div>
    );
  };

  const renderTotalWaterConsInOrgGraph = () => {
    return (
      <div className="card Energy_Produced_card border-top-0">
        <div className="Energy_Produced_card-header">
          <h6 className="m-0"> Total water consumption in the organization (in Megaliters)</h6>
          {renderGraphIconHolder(AnalyticsReportType.TOTAL_WATER_CONSUMPTION_IN_THE_ORGANIZATION)}
        </div>
        <div className="card-body pt-0 pb-2">
          <div className="">
            <Line data={linechartData} />
          </div>
          {/* <div className="text-center p-2 text-dark">
            Emission intensity by metal product <strong>1.01</strong> (t Scope 1 & Scope 2 Co2-e)
          </div> */}

          {/* <div className="Energy_Produced_card-footer mt-2">
            <div>
              <h5>1.01 kCO2 - e/oz</h5>
              <b>Emission intensity by metal product</b>
            </div>
            <div className="Compared_box01">
              <h6>+9.6%</h6>
              <b>Compared to 2022 Q4</b>
            </div>
          </div> */}
        </div>
      </div>
    )
  };

  const renderEnergyConsWithinOrgGraph = () => {
    return (
      <div className="card  Energy_Produced_card">
        <div className="Energy_Produced_card-header">
          <h6 className="m-0">Energy consumption with in the organization</h6>
          {
            renderGraphIconHolder(
              isDemoEnv && (companyId === 150 || companyId === 151)
              ? AnalyticsReportType.ENERGY_CONSUMPTION_WITHIN_THE_ORGANIZATION_V2
              : AnalyticsReportType.ENERGY_CONSUMPTION_WITHIN_THE_ORGANIZATION
            )
          }
        </div>
        <div className="card-body pt-0">
          <div className="">
            <Line data={linechartData} />
          </div>
          <div className="Energy_Produced_card-footer mt-2">
            <div>
              <h5>{graphFooter.total}</h5>
              <b>Average energy intake</b>
            </div>
            <div className="Compared_box01">
              <h6>{graphFooter.comparison}%</h6>
              <b>Compared to 2022 Q4</b>
            </div>
          </div>
        </div>

      </div>
    );
  };

  return (
    <>
    {type === CompanyDashboardGraphType.AVG_EENRGY_INT_BY_ORE_MILLED && renderEnergyIntensityOremilledGraph()}
    {type === CompanyDashboardGraphType.ENERGY_INT_BY_METAL_PROD && renderEnergyIntensityByMetalProd()}
    {type === CompanyDashboardGraphType.SCOPE_123_EMISSION && renderScope123EmissionGraph()}
    {type === CompanyDashboardGraphType.SOCIAL_EMPLOYMENT && renderSocialEmpGraph()}
    {type === CompanyDashboardGraphType.ECONO_VALUE_DISTRIBUTED && renderEconomicValueDistributedGraph()}
    {type === CompanyDashboardGraphType.ECONO_VALUE_GENERATED && renderEconomicValueGenGraph()}
    {type === CompanyDashboardGraphType.WASTE_DISPOSAL && renderWasteDisposalGraph()}
    {type === CompanyDashboardGraphType.ENERGY_PRODUCED && renderEnergyProducedGraph()}
    {type === CompanyDashboardGraphType.SUSTAINABLE_FINANCE && renderSustainableFinanceGraph()}
    {type === CompanyDashboardGraphType.ENERGY_CONS_OUTSIDE_ORG && renderEnergyConsumptionOutsideGraph()}
    {type === CompanyDashboardGraphType.WASTE_GEN_IN_ORG && renderWasteGeneratedInOrgGraph()}
    {type === CompanyDashboardGraphType.SCOPE_123_EMISSION_ACROSS_SITE && renderScope123EmissionAcrossSiteGraph()}
    {type === CompanyDashboardGraphType.TOTAL_WATER_CONS_IN_ORG && renderTotalWaterConsInOrgGraph()}
    {type === CompanyDashboardGraphType.ENERGY_CONS_WITHIN_ORG && renderEnergyConsWithinOrgGraph()}
    </>
  );
};
export default withTranslation()(CompanyGraphCard);
